import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { useOrbitSessions } from 'api/orbit';
import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import { OverwatchActionScope } from 'containers/Overwatch/actions/actionSubmissions';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import ForbiddenPage from 'pages/public/AccessDenied';

import { useOrbitContext } from '../OrbitContext';
import OrbitThemeScope from '../OrbitThemeScope';
import { TOrbit } from '../i18n/TModules';
import {
  ORBIT_NEW_CHAT_KEY,
  setOrbitPendingWorkspace,
  useOrbitPendingWorkspaces
} from '../orbitPendingWorkspace';
import { findOrbitWorkspace } from '../orbitWorkspaces';
import { orbitTokens } from '../styles';
import { OrbitSession, OrbitWorkspaceTag } from '../types';
import useOrbitAccess from '../useOrbitAccess';
import useOrbitChatActions from '../useOrbitChatActions';
import useOrbitChatDelete from '../useOrbitChatDelete';
import useOrbitConversations from '../useOrbitConversations';
import useOrbitOpenChat from '../useOrbitOpenChat';
import OrbitChatMenu from './components/OrbitChatMenu';
import OrbitContextDrawer from './components/OrbitContextDrawer';
import OrbitExploreMore from './components/OrbitExploreMore';
import { OrbitPageDemoChat, OrbitPageLiveChat } from './components/OrbitPageChat';
import OrbitPageChatHeader from './components/OrbitPageChatHeader';
import OrbitPageRail from './components/OrbitPageRail';
import OrbitWelcome from './components/OrbitWelcome';
import { pg } from './components/pageStyles';
import { ORBIT_PAGE_SHOWCASE, findOrbitPageDemoChat } from './orbitPageChatSeed';
import useOrbitUnsubscribedFeatures from './useOrbitUnsubscribedFeatures';
import useOrbitWorkspaceSelection, {
  OrbitWorkspaceSelectionContext
} from './useOrbitWorkspaceSelection';

export const ORBIT_PAGE_VIEW_PARAM = 'view';
export const ORBIT_PAGE_EXPLORE = 'explore';
export const ORBIT_PAGE_DEMO_PARAM = 'demo';
export const ORBIT_PAGE_FEATURE_PARAM = 'feature';

/** About the flyout's drawn thirteen rows twice over, so one page fills it. */
const WORKSPACE_FLYOUT_PAGE_SIZE = 25;

/** How long typing in the rail's search pauses before it is sent. */
const ORBIT_SEARCH_DEBOUNCE_MS = 300;

const PAGE_PARAMS = [ORBIT_PAGE_VIEW_PARAM, ORBIT_PAGE_DEMO_PARAM, ORBIT_PAGE_FEATURE_PARAM];

const Page = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
  background: ${pg.page};
  color: ${pg.text};
  font-family: ${orbitTokens.fontFamily};
`;

const Main = styled.main`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;

/**
 * Welcome and Explore More scroll on their own; the chat views bring their own scroller. Neither
 * has a top bar: the landing's chips start at y 21 (6114:4166), Explore's banner at y 37 (6180:2536).
 */
const ViewScroll = styled.div<{ $top: number }>`
  flex: 1 1 auto;
  min-height: 0;
  padding: ${({ $top }) => $top}px 24px 24px;
  overflow-y: auto;
  ${intelligenceScrollbar('y')}
`;

const OrbitPageView: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [contextOpen, setContextOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [openWorkspace, setOpenWorkspace] = useState<OrbitWorkspaceTag | null>(null);
  const [listedWorkspace, setListedWorkspace] = useState<OrbitWorkspaceTag | null>(null);
  const composerRef = useRef<HTMLInputElement>(null);
  const { attachPageSurface, newChat, sessionId, turns, activeTurnId, pendingQuestion } =
    useOrbitContext();
  const { t } = useMultiLanguageTranslation();
  const explore = params.get(ORBIT_PAGE_VIEW_PARAM) === ORBIT_PAGE_EXPLORE;
  const demo = findOrbitPageDemoChat(params.get(ORBIT_PAGE_DEMO_PARAM));
  const focusFeatureId = params.get(ORBIT_PAGE_FEATURE_PARAM);
  const unsubscribed = useOrbitUnsubscribedFeatures();
  const workspaceSelection = useOrbitWorkspaceSelection(demo?.id ?? null);
  const pending = useOrbitPendingWorkspaces();
  const openChat = useOrbitOpenChat();
  const actions = useOrbitChatActions();
  const { select, ...recent } = useOrbitConversations({ workspace: 'none' });
  const pinned = useOrbitSessions({ pinned: true });
  // The last Workspace opened stays listed after its flyout closes: a menu press elsewhere closes
  // it, and the change that menu makes must still reach the list it reopens to.
  const workspaceChats = useOrbitSessions(
    listedWorkspace === null
      ? null
      : { workspace: listedWorkspace, size: WORKSPACE_FLYOUT_PAGE_SIZE }
  );
  const searchResults = useOrbitSessions(searchTerm === '' ? null : { q: searchTerm });
  const hasConversation =
    sessionId !== null || turns.length > 0 || activeTurnId !== null || pendingQuestion !== null;

  useEffect(() => attachPageSurface(), [attachPageSurface]);

  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchQuery.trim()), ORBIT_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const showView = useCallback(
    (view: { explore?: boolean; demo?: string; feature?: string }) => {
      const updated = new URLSearchParams(params);
      PAGE_PARAMS.forEach(param => updated.delete(param));
      if (view.explore) {
        updated.set(ORBIT_PAGE_VIEW_PARAM, ORBIT_PAGE_EXPLORE);
        if (view.feature !== undefined) {
          updated.set(ORBIT_PAGE_FEATURE_PARAM, view.feature);
        }
      }
      if (view.demo !== undefined) {
        updated.set(ORBIT_PAGE_DEMO_PARAM, view.demo);
      }
      setParams(updated);
    },
    [params, setParams]
  );

  // A question asked from a demo or the catalogue is a live one: show the live chat.
  useEffect(() => {
    if (pendingQuestion !== null) {
      setParams(current => {
        const updated = new URLSearchParams(current);
        PAGE_PARAMS.forEach(param => updated.delete(param));
        return updated;
      });
    }
  }, [pendingQuestion, setParams]);

  const startNewChat = useCallback(() => {
    setContextOpen(false);
    setOpenWorkspace(null);
    setOrbitPendingWorkspace(ORBIT_NEW_CHAT_KEY, null);
    newChat();
    showView({});
  }, [newChat, showView]);

  const selectChat = useCallback(
    (session: OrbitSession) => {
      setOpenWorkspace(null);
      select(session);
      showView({});
    },
    [select, showView]
  );

  const chatOpen = !explore && (demo !== undefined || hasConversation);
  const chatTitle = demo?.title ?? openChat.title ?? t(`${TOrbit}.page.newChat`);

  const openContext = useCallback(() => setContextOpen(true), []);
  const closeWorkspace = useCallback(() => setOpenWorkspace(null), []);
  const closeContext = useCallback(() => setContextOpen(false), []);

  const showDeleteChat = useOrbitChatDelete();
  // The open chat gives way to the new-chat view; its composer takes the focus.
  const confirmDelete = useCallback(
    (deletedId: string, title: string) =>
      showDeleteChat(deletedId, title, () => composerRef.current?.focus()),
    [showDeleteChat]
  );

  const renderChatMenu = (session: OrbitSession, titleId: string) => (
    <OrbitChatMenu
      anchor="row"
      describedBy={titleId}
      onRename={() => actions.rename(session)}
      pin={{ pinned: session.pinned, onToggle: () => actions.setPinned(session, !session.pinned) }}
      onDelete={() => confirmDelete(session.session_id, session.title)}
      workspace={{ current: session.workspace, onChoose: tag => actions.file(session, tag) }}
      testId="orbit-page-rail-chat-menu"
    />
  );

  // Every open chat draws the "…" (6410:9002). A demo, or a chat the server does not list yet, has
  // no row to rename or pin; a first question still waiting for its session has nothing to delete.
  const record = demo === undefined ? openChat.record : undefined;
  const headerMenu = (
    <OrbitChatMenu
      anchor="header"
      onRename={record !== undefined ? () => actions.rename(record) : null}
      pin={
        record !== undefined
          ? { pinned: record.pinned, onToggle: () => actions.setPinned(record, !record.pinned) }
          : null
      }
      onDelete={
        demo === undefined && sessionId !== null ? () => confirmDelete(sessionId, chatTitle) : null
      }
      workspace={{ current: workspaceSelection.workspace, onChoose: workspaceSelection.choose }}
      testId="orbit-page-chat-menu"
    />
  );
  const chatWorkspace = findOrbitWorkspace(workspaceSelection.workspace);
  const chatSubtitle =
    chatWorkspace !== undefined
      ? t(`${TOrbit}.page.addedWorkspace`, { name: chatWorkspace.title })
      : null;

  // A chat waiting to be filed is about to leave Recent; it is not drawn there in between.
  const recentChats = {
    ...recent,
    sessions: recent.sessions.filter(session => pending[session.session_id] === undefined)
  };

  // Keyed per view (and per demo), so no view inherits another's scroll position.
  const renderMain = (): React.ReactNode => {
    if (explore) {
      return (
        <ViewScroll key="explore" $top={37} data-testid="orbit-page-scroll">
          <OrbitExploreMore state={unsubscribed} focusFeatureId={focusFeatureId} />
        </ViewScroll>
      );
    }
    if (demo !== undefined) {
      return <OrbitPageDemoChat key={demo.id} demo={demo} />;
    }
    if (hasConversation) {
      return <OrbitPageLiveChat />;
    }
    return (
      <ViewScroll key="welcome" $top={21} data-testid="orbit-page-scroll">
        <OrbitWelcome onOpenContext={openContext} composerRef={composerRef} />
      </ViewScroll>
    );
  };

  return (
    <OrbitThemeScope>
      <OrbitWorkspaceSelectionContext.Provider value={workspaceSelection}>
        <Page data-testid="orbit-page">
          <OrbitPageRail
            onNewChat={startNewChat}
            unsubscribed={unsubscribed}
            onOpenUnsubscribed={feature => showView({ explore: true, feature })}
            recent={recentChats}
            pinned={pinned}
            search={{
              query: searchQuery,
              onQueryChange: setSearchQuery,
              results: searchQuery.trim() === '' ? null : searchResults
            }}
            activeSessionId={explore || demo !== undefined ? null : sessionId}
            onSelectChat={selectChat}
            openWorkspace={openWorkspace}
            workspaceChats={openWorkspace === null ? null : workspaceChats}
            onCloseWorkspace={closeWorkspace}
            chatWorkspace={explore ? null : workspaceSelection.workspace}
            onOpenWorkspace={tag => {
              setListedWorkspace(tag);
              setOpenWorkspace(current => (current === tag ? null : tag));
            }}
            renderRowMenu={renderChatMenu}
            pinnedDemos={[ORBIT_PAGE_SHOWCASE]}
            activeDemoId={explore ? null : demo?.id ?? null}
            onSelectDemo={id => showView({ demo: id })}
          />
          <Main>
            {chatOpen ? (
              <OrbitPageChatHeader title={chatTitle} subtitle={chatSubtitle} menu={headerMenu} />
            ) : null}
            <Content>{renderMain()}</Content>
          </Main>
          {contextOpen ? <OrbitContextDrawer onClose={closeContext} /> : null}
        </Page>
      </OrbitWorkspaceSelectionContext.Provider>
    </OrbitThemeScope>
  );
};

/**
 * Full-page Orbit, behind the same gate as the panel (`useOrbitAccess`: Perx Orbit flag under the
 * AI Workspace master flag, plus `customer_intelligence.ai_assistant` use). A denied user gets the
 * route-level 403 every other gated page shows.
 */
const OrbitFullPage: React.FC = () => {
  const access = useOrbitAccess();

  if (access === 'loading') {
    return null;
  }
  if (access === 'denied') {
    return <ForbiddenPage />;
  }
  // Leaving the page cancels any Book a Demo resend still pending; the chat and Explore views share it.
  return (
    <OverwatchActionScope>
      <OrbitPageView />
    </OverwatchActionScope>
  );
};

export default OrbitFullPage;
